/**
 * @module RecommendedArticles
 */
import React from 'react';
import { callSegmentTrack } from '@lifechurch/web-tools-io/dist/utils/helpers/analytics';
import { Log } from '@lifechurch/web-tools-io/dist/utils/helpers/browserLogger';
import useAuth from '@lifechurch/web-tools-io/dist/hooks/useAuth';
import useWindowSize from '@lifechurch/web-tools-io/dist/hooks/useWindowSize';
import { fetchMultipleArticlesByUUID } from '../../helpers/dataFetchers/findsFetchers';
import { ACTIONS, EVENTS } from '../../helpers/constants';
import { getAppliedTheme } from '../../helpers/getAppliedTheme';
import useTheme from '../../hooks/useTheme';
import RecommendedArticleCard from './RecommendedArticleCard';
import './RecommendedArticles.scss';

/**
 * Represents a container to hold recommended articles cards.
 *
 * @param {object} props - The component props object.
 * @param {Array} props.articles - Array of article uuid values for which to include cards.
 * @param {string} props.theme - The theme value.
 * @param {string} props.title - Title value to use for the component heading.
 *
 * @returns {React.ReactElement} The RecommendedArticles component.
 */
function RecommendedArticles({ articles, theme, title = 'Recommended' }) {
  const { user } = useAuth();
  const { isMobile } = useWindowSize();
  const { pageTheme } = useTheme();
  const [data, setData] = React.useState(null);
  const appliedTheme = getAppliedTheme(pageTheme, theme);

  /**
   * Handler function for link click event.
   *
   * @param {Event} event - The Event object associated with the click.
   */
  function handleLinkClick(event) {
    callSegmentTrack({
      event: EVENTS.buttonAction,
      properties: {
        action: ACTIONS.clicked,
        component: 'Recommended Articles',
        component_url: event?.currentTarget?.getAttribute('href'),
        label: event?.currentTarget?.textContent,
        logged_in: !!user,
        preferred_campus: null,
        referrer: document?.referrer || null,
        title: document?.title || '',
        url: window?.location?.href,
        user_id: user?.['https://www.life.church/rock_person_alias_id'],
      },
    });
  }

  /**
   * Convenience effect to fetch and set data for the component.
   */
  React.useEffect(() => {
    async function fetchData() {
      try {
        const articlesResult = await fetchMultipleArticlesByUUID(articles);
        setData(articlesResult);
      } catch (error) {
        /* istanbul ignore next */
        Log.error(error);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles]);

  return (
    <>
      {data ? (
        <div className="recommended-articles-wrap container">
          <div
            className={`recommended-articles ${
              isMobile ? 'vertical' : 'horizontal'
            } ${appliedTheme}`}
            data-testid="lc-recommended-articles"
            id="id-recommended-articles"
          >
            <div className="title-wrap">
              <h3>{title}</h3>
            </div>
            <div className="content-wrap">
              {data.map((article) => {
                return (
                  <RecommendedArticleCard
                    articleData={article}
                    key={article['@id']}
                    onClick={handleLinkClick}
                  />
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default RecommendedArticles;
